
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, UserFocusIcon, SunIcon, GlassesIcon, HatIcon, WatchIcon, ShoppingBagIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';

interface CreativeToolsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyTool: (toolId: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => void;
    isLoading: boolean;
}

const CreativeToolsPanel: React.FC<CreativeToolsPanelProps> = ({ isOpen, onClose, onApplyTool, isLoading }) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const tools = [
        {
            id: 'ageing' as const,
            labelKey: 'creative_tool_ageing_label',
            descKey: 'creative_tool_ageing_desc',
            icon: <UserFocusIcon className="w-5 h-5" />,
            color: 'text-indigo-600 dark:text-indigo-400'
        },
        {
            id: 'wrinkles' as const,
            labelKey: 'creative_tool_wrinkles_label',
            descKey: 'creative_tool_wrinkles_desc',
            icon: <SparklesIcon className="w-5 h-5" />,
            color: 'text-emerald-600 dark:text-emerald-400'
        },
        {
            id: 'golden_hour' as const,
            labelKey: 'creative_tool_golden_hour_label',
            descKey: 'creative_tool_golden_hour_desc',
            icon: <SunIcon className="w-5 h-5" />,
            color: 'text-amber-500 dark:text-amber-400'
        },
        {
            id: 'remove_glasses' as const,
            labelKey: 'creative_tool_remove_glasses_label',
            descKey: 'creative_tool_remove_glasses_desc',
            icon: <GlassesIcon className="w-5 h-5" />,
            color: 'text-blue-500 dark:text-blue-400'
        },
        {
            id: 'remove_hat' as const,
            labelKey: 'creative_tool_remove_hat_label',
            descKey: 'creative_tool_remove_hat_desc',
            icon: <HatIcon className="w-5 h-5" />,
            color: 'text-orange-500 dark:text-orange-400'
        },
        {
            id: 'remove_watch' as const,
            labelKey: 'creative_tool_remove_watch_label',
            descKey: 'creative_tool_remove_watch_desc',
            icon: <WatchIcon className="w-5 h-5" />,
            color: 'text-slate-500 dark:text-slate-400'
        },
        {
            id: 'remove_bag' as const,
            labelKey: 'creative_tool_remove_bag_label',
            descKey: 'creative_tool_remove_bag_desc',
            icon: <ShoppingBagIcon className="w-5 h-5" />,
            color: 'text-pink-500 dark:text-pink-400'
        }
    ];

    return (
        <div className="absolute top-16 right-4 z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-72 max-h-[80vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-white uppercase text-xs tracking-wider">
                    {t('creative_tools_title', 'Creative Tools')}
                </h3>
                <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors" title={t('common_close')}>
                    <XIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-3">
                {tools.map(tool => (
                    <button
                        key={tool.id}
                        onClick={() => onApplyTool(tool.id)}
                        disabled={isLoading}
                        className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-md transition-all group disabled:opacity-50"
                    >
                        <div className={`p-2 rounded-full bg-gray-50 dark:bg-gray-900 group-hover:scale-110 transition-transform ${tool.color}`}>
                            {tool.icon}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{t(tool.labelKey)}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                {t(tool.descKey)}
                            </p>
                        </div>
                    </button>
                ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                    {t('creative_tools_disclaimer', 'Results are AI-generated and may vary based on photo quality.')}
                </p>
            </div>
        </div>
    );
};

export default CreativeToolsPanel;
